import type { MessagingPlatform } from '../messagingPlatform.enum';
import type { PersonOrigin } from '../person';
import {
  QueueMessageType,
  TriggerKey,
  UserCommunicationAgentQueueBodyBase,
} from '../userCommunicationAgent';

export interface PersonEventQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.PERSON_EVENT;
  triggerKey: TriggerKey.PERSON_CREATED | TriggerKey.LANDING_PAGE_LEAD_CREATED;
  personId: string;
  origin: MessagingPlatform | PersonOrigin;
  createdAt: number;
  // Present = the contact was created in a TEAM# partition. In that case the
  // inherited userId carries the TEAM id, not a user — the same convention
  // enqueueKnowledgePageEvent already uses, which processRecord tolerates
  // because ResolvedAgent.userId takes precedence over body.userId and the
  // team resolution path always sets it.
  teamId?: string;
}

export interface PersonCreatedTriggerConfig {
  // Absent means true. Triggers persisted before this feature have no
  // triggerConfig at all and must keep firing for personal contacts exactly
  // as today — this is the backward-compatibility contract.
  includePersonal?: boolean;
  teamIds?: string[];
}

export const matchesPersonCreatedTrigger = (
  config: PersonCreatedTriggerConfig | undefined,
  event: { teamId?: string },
): boolean =>
  event.teamId
    ? (config?.teamIds ?? []).includes(event.teamId)
    : config?.includePersonal !== false;

export enum NotesWriteMode {
  APPEND = 'APPEND',
  REPLACE = 'REPLACE',
}

// Fields are {{variable}} templates, resolved by the strategy at runtime.
export interface SearchPeopleStepConfig {
  name?: string;
  email?: string;
  jobTitle?: string;
  // Absent means the flow owner's personal People; present scopes the search
  // to that team's contacts instead.
  teamId?: string;
}

export interface UpdatePersonNotesStepConfig {
  personId: string;
  notesTemplate: string;
  // default APPEND is applied in the strategy, not here
  mode?: NotesWriteMode;
  // Absent means the flow owner's personal People; present scopes the
  // update to that team's contact instead.
  teamId?: string;
}

export interface LoadTeamPeopleStepConfig {
  teamId: string;
  // with query -> searchTeamPeople; without -> listTeamPeople
  query?: string;
}

// Fields are {{variable}} templates, resolved by the strategy at runtime.
export interface CreatePersonEventStepConfig {
  personId?: string;
  personEmail?: string;
  titleTemplate: string;
  descriptionTemplate?: string;
  // Absent means the flow owner's personal People; present scopes the event
  // to that team's contact instead.
  teamId?: string;
}
